import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import moment from "moment";
import { URL } from "../../variables";
import { logout } from "../../actions/AppActions";

const criterios = {
  sisu: "Sistema de Seleção Unificada – SISU",
  prouni_fies: "PROUNI / FIES",
  faculdade_privada_enem: "Faculdade Privada / ENEM",
  estagio_jovem_aprendiz: "Estágio / Jovem Aprendiz",
  emprego_privado: "Emprego Privado",
  concurso: "Concurso",
};

const formatCpf = (cpf) => String(cpf || "").replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");

export default function RelatorioTentativas() {
  const token = useSelector((state) => state.AppReducer.token);
  const dispatch = useDispatch();
  const [filters, setFilters] = useState({ nome: "", cpf: "", criterio_selecao: "" });
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ totalPages: 1, totalItems: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ ...filters, page, per_page: 15 });
        const response = await fetch(`${URL}api/inscricoes/tentativas-prova?${params}`, {
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        const result = await response.json();
        if (response.status === 401 || result.message === "Unauthenticated.") {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          dispatch(logout());
          return;
        }
        if (!response.ok || result.result === false) {
          throw new Error(result.message || result.error?.message || "Não foi possível carregar o relatório.");
        }
        if (!controller.signal.aborted) {
          setRows(result.data.tentativas);
          setPagination(result.data.pagination);
        }
      } catch (err) {
        if (!controller.signal.aborted) setError(err.message || "Não foi possível carregar o relatório.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 400);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [token, dispatch, filters, page, retry]);

  const changeFilter = (field, value) => {
    setFilters((previous) => ({ ...previous, [field]: value }));
    setPage(1);
  };

  return (
    <div className="row">
      <div className="col-12">
        <div className="card"><div className="card-body">
          <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
            <h4 className="header-title" style={{ fontSize: "1.5rem", color: "black" }}>Tentativas de prova por candidato</h4>
            <Link to="/inscricoes/avaliar">Ver inscrições</Link>
          </div>
          <p>Histórico de tentativas por CPF e critério de seleção, incluindo resultados arquivados. São contados apenas os registros com aceite confirmado, com a tentativa mais recente primeiro.</p>
          <div className="row mb-4">
            <div className="col-md-4 mb-2">
              <label htmlFor="tentativas-nome">Nome do candidato</label>
              <input id="tentativas-nome" className="form-control" placeholder="Pesquisar por nome" value={filters.nome} onChange={(event) => changeFilter("nome", event.target.value)} />
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="tentativas-cpf">CPF do candidato</label>
              <input id="tentativas-cpf" className="form-control" placeholder="CPF completo ou parcial" inputMode="numeric" maxLength={14} value={filters.cpf} onChange={(event) => changeFilter("cpf", event.target.value.replace(/[^\d.\-\s]/g, ""))} />
            </div>
            <div className="col-md-5 mb-2">
              <label htmlFor="tentativas-criterio">Critério de seleção</label>
              <select id="tentativas-criterio" className="form-control" value={filters.criterio_selecao} onChange={(event) => changeFilter("criterio_selecao", event.target.value)}>
                <option value="">Todos os critérios</option>
                {Object.entries(criterios).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
          </div>
          {error && <div className="alert alert-danger" role="alert">{error} <button type="button" className="btn btn-sm btn-outline-danger ms-2" onClick={() => setRetry((value) => value + 1)}>Tentar novamente</button></div>}
          <div className="table-responsive" aria-busy={loading}>
            <table className="table table-striped">
              <thead><tr><th>Nome do candidato</th><th>CPF</th><th>Critério de seleção</th><th>Tentativas</th><th>Última prova</th></tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={5} className="text-center"><CircularProgress size={28} aria-label="Carregando relatório" /></td></tr> : error ? null : rows.length === 0 ? <tr><td colSpan={5} className="text-center">Nenhuma tentativa encontrada para os filtros informados.</td></tr> : rows.map((row) => (
                  <tr key={`${row.cpf}-${row.criterio_selecao}`}>
                    <td>{row.nome_completo}</td><td>{formatCpf(row.cpf)}</td><td>{criterios[row.criterio_selecao] || row.criterio_selecao || "Não informado"}</td>
                    <td><strong>{row.total_tentativas}</strong></td><td>{row.ultima_prova ? moment(row.ultima_prova).format("DD/MM/YYYY HH:mm") : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && !error && <div className="d-flex justify-content-between align-items-center flex-wrap">
            <span>{pagination.totalItems} registro(s) por candidato e critério</span>
            <Pagination count={pagination.totalPages} page={page} onChange={(event, value) => setPage(value)} color="primary" />
          </div>}
        </div></div>
      </div>
    </div>
  );
}
